import { useCallback, useRef, useState } from 'react';

/**
 * Keeps the map marker and teaser list in sync.
 * - Clicking a marker scrolls the list to the teaser and marks it current.
 * - When a teaser comes into view, it becomes current unless
 *   we are in the middle of a programmatic scroll-to-target.
 */
const useTopListMap = (
  teaserRefs: React.RefObject<Record<string, HTMLDivElement | null>>,
): {
  currentArticleId: string | null;
  setCurrentArticleId: (id: string | null) => void;
  handleMarkerClick: (articleId: string) => void;
  handleTeaserView: (articleId: string) => void;
} => {
  const [currentArticleId, setCurrentArticleId] = useState<string | null>(null);
  const scrollTargetIdRef = useRef<string | null>(null);

  const handleMarkerClick = useCallback(
    (articleId: string): void => {
      scrollTargetIdRef.current = articleId;
      setCurrentArticleId(articleId);

      const teaserEl = teaserRefs.current[articleId];

      if (teaserEl) {
        teaserEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    [teaserRefs],
  );

  const handleTeaserView = useCallback(
    (articleId: string): void => {
      const pendingTarget = scrollTargetIdRef.current;

      if (pendingTarget) {
        if (articleId !== pendingTarget) {
          return;
        }

        scrollTargetIdRef.current = null;
      }

      if (articleId === currentArticleId) {
        return;
      }

      setCurrentArticleId(articleId);
    },
    [currentArticleId],
  );

  return {
    currentArticleId,
    setCurrentArticleId,
    handleMarkerClick,
    handleTeaserView,
  };
};

export { useTopListMap };
